import { browser } from '$app/environment';
import { invalidate } from '$app/navigation';
import { scrollToActiveRace } from '$components/race/RaceList.svelte';
import type { Race } from '$types/races';
import { get, writable } from 'svelte/store';

export interface RaceFilters {
  ids: string[];
  meetings: string[];
  types: string[];
  dates: string[];
  locations?: string[];
}

// track if the user is currently changing filters
// will show toggle boxes and filter dropdowns
export const isFiltering = writable(false);

// track the current filtered races and sync with localstorage
export const defaultRaceFilter = {
  types: [],
  dates: [],
  meetings: [],
  ids: [],
  locations: []
} as RaceFilters;
const rfStore = browser ? localStorage.getItem('raceFilters') : null;
const storedRaceFilters = rfStore ? JSON.parse(rfStore) : defaultRaceFilter;

function createRaceFilterStore() {
  const { subscribe, set, update } = writable(
    (storedRaceFilters as RaceFilters) || (defaultRaceFilter as RaceFilters)
  );

  const toggleRaceTypeFilter = (type: 'G' | 'H' | 'R' | 'T') => {
    // accept both T and R for racing
    if (type === 'R') type = 'T';
    update((filters) => {
      const index = filters.types.indexOf(type);
      if (index > -1) {
        filters.types.splice(index, 1);
      } else {
        filters.types.push(type);
      }
      return filters;
    });
  };

  const toggleRaceMeetingFilter = (meeting: Race['meeting']) => {
    update((filters) => {
      const key = `${meeting.type}-${meeting.track}-${meeting.location}`;
      const index = filters.meetings.indexOf(key);
      if (index > -1) {
        filters.meetings.splice(index, 1);
      } else {
        filters.meetings.push(key);
      }
      return filters;
    });
  };

  const toggleRaceLocationFilter = (location: Race['meeting']['location']) => {
    update((filters) => {
      // check that locations exists
      if (!filters.locations) {
        filters.locations = [];
      }
      const index = filters.locations?.indexOf(location);
      if (index > -1) {
        filters.locations?.splice(index, 1);
      } else {
        filters.locations?.push(location);
      }
      return filters;
    });
  };

  const updateRaceDateFilter = (date: Date | null) => {
    update((filters) => {
      // cast Date to string
      if (date) {
        // add local timezone to date
        const tzDate = new Date(
          date.getTime() - date.getTimezoneOffset() * 60000
        );
        filters.dates = [tzDate.toISOString().split('T')[0]];
      } else {
        filters.dates = [];
      }
      return filters;
    });
  };

  const toggleRaceIdFilter = (id: string) => {
    update((filters) => {
      const index = filters.ids.indexOf(id);
      if (index > -1) {
        filters.ids.splice(index, 1);
      } else {
        filters.ids.push(id);
      }
      return filters;
    });
  };

  function clearFilters() {
    set(defaultRaceFilter);
    setTimeout(async () => {
      await invalidate('betwatch:races');
      setTimeout(() => {
        get(scrollToActiveRace)();
      }, 100);
    }, 200);
  }

  function isEmpty() {
    return (
      defaultRaceFilter.types.length === 0 &&
        defaultRaceFilter.dates.length === 0 &&
        defaultRaceFilter.meetings.length === 0 &&
        defaultRaceFilter.ids.length === 0,
      defaultRaceFilter.locations?.length === 0
    );
  }

  return {
    isEmpty,
    subscribe,
    set,
    update,
    toggleRaceTypeFilter: (type: 'G' | 'H' | 'R' | 'T') =>
      toggleRaceTypeFilter(type),
    toggleRaceMeetingFilter: (meeting: Race['meeting']) =>
      toggleRaceMeetingFilter(meeting),
    updateRaceDateFilter: (date: Date | null) => updateRaceDateFilter(date),
    toggleRaceIdFilter: (id: string) => toggleRaceIdFilter(id),
    toggleRaceLocationFilter: (location: Race['meeting']['location']) =>
      toggleRaceLocationFilter(location),
    clearFilters: () => clearFilters()
  };
}

export const raceFilters = createRaceFilterStore();
browser &&
  raceFilters.subscribe((value) =>
    browser ? localStorage.setItem('raceFilters', JSON.stringify(value)) : null
  );
